import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/components/ui/card";
import {
  Package,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Settings,
  Clock,
  CheckCircle,
  XCircle,
  ShoppingCart,
  Download,
  Upload,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import HeadLayout from "@shared/layouts/HeadLayout";
import { StockAnalyticsDashboard } from "@features/products/components/StockAnalyticsDashboard";
import { BulkStockManager } from "@features/products/components/BulkStockManager";
import { StockAlerts } from "@features/products/components/StockAlerts";
import { LowStockDashboard } from "@features/products/components/LowStockDashboard";
import { RecentStockMovements } from "@features/products/components/RecentStockMovements";
import { DEFAULT_VALUES, TIME_INTERVALS } from "@shared/utils/constants";

export default function EnhancedStockManagement() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("analytics");

  return (
    <>
      <HeadLayout title="products.enhancedStockManagement" />
      <div className="max-w-screen-2xl mx-auto flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("products.enhancedStockManagement")}
            </h1>
            <p className="text-muted-foreground">
              {t("products.enhancedStockManagementDescription")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <BulkStockManager
              trigger={
                <Button>
                  <Edit className="w-4 h-4 mr-2" />
                  {t("products.bulkStockUpdate")}
                </Button>
              }
            />
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              {t("products.exportStockReport")}
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("products.totalProducts")}
              </CardTitle>
              <Package className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">--</div>
              <p className="text-xs text-muted-foreground">
                {t("products.inInventory")}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("products.lowStockProducts")}
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">--</div>
              <p className="text-xs text-muted-foreground">
                {t("products.requiresAttention")}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("products.recentMovements")}
              </CardTitle>
              <Activity className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">--</div>
              <p className="text-xs text-muted-foreground">
                {t("products.last24Hours")}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("products.activeAlerts")}
              </CardTitle>
              <Bell className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">--</div>
              <p className="text-xs text-muted-foreground">
                {t("products.requiresAction")}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              {t("products.analytics")}
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              {t("products.alerts")}
            </TabsTrigger>
            <TabsTrigger value="low-stock" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t("products.lowStockMonitoring")}
            </TabsTrigger>
            <TabsTrigger value="movements" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              {t("products.recentMovements")}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              {t("products.settings")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="space-y-4">
            <StockAnalyticsDashboard />
          </TabsContent>

          <TabsContent value="alerts" className="space-y-4">
            <StockAlerts />
          </TabsContent>

          <TabsContent value="low-stock" className="space-y-4">
            <LowStockDashboard />
          </TabsContent>

          <TabsContent value="movements" className="space-y-4">
            <RecentStockMovements />
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  {t("products.stockManagementSettings")}
                </CardTitle>
                <CardDescription>
                  {t("products.stockManagementSettingsDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Default Thresholds */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        {t("products.defaultLowStockThreshold")}
                      </label>
                      <input
                        type="number"
                        min="0"
                        defaultValue={DEFAULT_VALUES.LOW_STOCK_THRESHOLD.toString()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        {t("products.defaultCriticalThreshold")}
                      </label>
                      <input
                        type="number"
                        min="0"
                        defaultValue={DEFAULT_VALUES.CRITICAL_STOCK_THRESHOLD.toString()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        {t("products.defaultRefreshInterval")} (s)
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-md">
                        {TIME_INTERVALS.REFRESH_INTERVALS.map((interval) => (
                          <option 
                            key={interval.value} 
                            value={interval.value}
                            selected={interval.value === DEFAULT_VALUES.REFRESH_INTERVAL_SECONDS}
                          >
                            {interval.value} {t("common.seconds")}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Notification Settings */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">
                      {t("products.notificationSettings")}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <input type="checkbox" id="email-alerts" defaultChecked />
                        <label htmlFor="email-alerts" className="text-sm">
                          {t("products.emailAlerts")}
                        </label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input type="checkbox" id="browser-notifications" defaultChecked />
                        <label htmlFor="browser-notifications" className="text-sm">
                          {t("products.browserNotifications")}
                        </label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input type="checkbox" id="critical-alerts" defaultChecked />
                        <label htmlFor="critical-alerts" className="text-sm">
                          {t("products.criticalStockAlerts")}
                        </label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input type="checkbox" id="movement-notifications" />
                        <label htmlFor="movement-notifications" className="text-sm">
                          {t("products.stockMovementNotifications")}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Export Settings */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">
                      {t("products.exportSettings")}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          {t("products.defaultExportFormat")}
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-md">
                          <option value="excel">Excel (.xlsx)</option>
                          <option value="csv">CSV</option>
                          <option value="pdf">PDF</option>
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          {t("products.includeInExports")}
                        </label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input type="checkbox" id="include-movements" defaultChecked />
                            <label htmlFor="include-movements" className="text-sm">
                              {t("products.stockMovements")}
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input type="checkbox" id="include-history" defaultChecked />
                            <label htmlFor="include-history" className="text-sm">
                              {t("products.stockHistory")}
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input type="checkbox" id="include-analytics" />
                            <label htmlFor="include-analytics" className="text-sm">
                              {t("products.analyticsData")}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button>
                      <Settings className="w-4 h-4 mr-2" />
                      {t("products.saveSettings")}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
} 